import { useRef, useState } from 'react';
import { ApiError } from '@geefamily/api';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { useRemoveAvatar, useUploadAvatar } from '../api';

/** Matches what the server's decoder will accept. */
const AcceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MaxBytes = 5 * 1024 * 1024;

interface AvatarUploaderProps {
  avatarUrl: string | null | undefined;
  userName: string;
}

export function AvatarUploader({ avatarUrl, userName }: AvatarUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const upload = useUploadAvatar();
  const remove = useRemoveAvatar();

  const shown = preview ?? avatarUrl ?? null;

  function pick(file: File | undefined) {
    setLocalError(null);

    if (!file) {
      return;
    }

    // Client-side checks are for the user's benefit — instant feedback instead
    // of a round-trip. The server re-checks everything, including whether the
    // bytes are really an image, which this cannot know.
    if (!AcceptedTypes.includes(file.type)) {
      setLocalError('Yalnızca JPEG, PNG, WebP veya GIF yükleyebilirsiniz.');
      return;
    }

    if (file.size > MaxBytes) {
      setLocalError('Görsel en fazla 5 MB olabilir.');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    upload.mutate(file, {
      onSettled: () => {
        // Revoke as soon as the server URL takes over, or every upload leaks a
        // blob for the lifetime of the tab.
        URL.revokeObjectURL(objectUrl);
        setPreview(null);
      },
    });
  }

  const errorMessage =
    localError ??
    (upload.isError
      ? upload.error instanceof ApiError
        ? upload.error.message
        : 'Görsel yüklenemedi.'
      : null);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
      <div className="relative size-24 shrink-0 overflow-hidden rounded-full border border-surface-700 bg-surface-800">
        {shown ? (
          <img
            src={shown}
            alt={`${userName} profil fotoğrafı`}
            className="size-full object-cover"
            width={96}
            height={96}
          />
        ) : (
          <span className="grid size-full place-items-center text-2xl font-semibold text-surface-500">
            {userName.slice(0, 1).toUpperCase()}
          </span>
        )}

        {upload.isPending ? (
          <span
            className="absolute inset-0 grid place-items-center bg-surface-950/70"
            role="status"
            aria-label="Yükleniyor"
          >
            <span
              aria-hidden
              className="size-6 animate-spin rounded-full border-2 border-surface-600 border-t-brand-400"
            />
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => inputRef.current?.click()}
            loading={upload.isPending}
          >
            {avatarUrl ? 'Fotoğrafı değiştir' : 'Fotoğraf yükle'}
          </Button>

          {avatarUrl ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => remove.mutate()}
              loading={remove.isPending}
            >
              Kaldır
            </Button>
          ) : null}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={AcceptedTypes.join(',')}
          className="sr-only"
          onChange={(event) => {
            pick(event.target.files?.[0]);
            // Reset, so picking the same file twice fires change again.
            event.target.value = '';
          }}
        />

        <p className="text-xs text-surface-500">
          JPEG, PNG, WebP veya GIF · en fazla 5 MB. Görsel kare olarak kırpılır ve 512&times;512
          WebP olarak saklanır.
        </p>

        {errorMessage ? <Alert tone="error">{errorMessage}</Alert> : null}
      </div>
    </div>
  );
}
